import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useAdmin } from "@/hooks/useAdmin";
import { useIsPro } from "@/hooks/useIsPro";
import { useEntitlement, type EntitlementInfo } from "@/hooks/useEntitlement";
import { openExternal } from "@/lib/openExternal";
import {
  EXTERNAL_SUPABASE_URL,
  EXTERNAL_SUPABASE_PUBLISHABLE_KEY,
} from "@/integrations/external-supabase/client";

// Brand periwinkle + type conventions, matching the rest of Settings.
const PERIWINKLE = "#5050C8";
const INK = "#1C1A3E";
const MUTED = "#54525C";

const CREATE_PORTAL_URL = `${EXTERNAL_SUPABASE_URL}/functions/v1/create-portal-session`;

/**
 * Portal failure carrying whether its message is fit for a mom's eyes.
 * `showToUser: true` means the server wrote that string in brand voice on
 * purpose (the no-billing-account case); anything else is plumbing and gets
 * swapped for the generic line, so no raw Stripe/API text ever reaches her.
 */
class PortalError extends Error {
  showToUser: boolean;
  constructor(message: string, showToUser: boolean) {
    super(message);
    this.name = "PortalError";
    this.showToUser = showToUser;
  }
}

/**
 * Renewal / expiration line for the Pro badge, sourced from entitlements.
 * Returns null when there is no date to show (admin comp / admin-preview with
 * no real entitlements row → expires_at IS NULL) so the badge renders link-only
 * with no placeholder text and no crash.
 *
 *   - Stripe annual (has a subscription object): "Renews July 12, 2027"
 *   - Founding Member (Stripe, one-time, no subscription): "Founding Member · renews July 12, 2027"
 *   - expires_at null: null (omit the line)
 */
export function renewalLabel(info: EntitlementInfo): string | null {
  if (!info.expiresAt) return null;
  const parsed = new Date(info.expiresAt);
  if (Number.isNaN(parsed.getTime())) return null;
  // Format in UTC: expires_at is stored as a UTC instant (e.g.
  // "2027-07-12T00:00:00Z"). Without timeZone: "UTC", toLocaleDateString uses
  // the machine's local zone, so any negative-offset zone (all of the US) rolls
  // "2027-07-12T00:00:00Z" back to July 11 — the off-by-one this fixes (#212).
  const date = parsed.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
  // Founding Member = Stripe source with a one-time payment, so no subscription
  // object was ever created (see stripe-webhook: founding never sets
  // stripe_subscription_id). Annual always carries a subscription id.
  const isFounding = info.source === "stripe" && !info.subscriptionId;
  return isFounding ? `Founding Member · renews ${date}` : `Renews ${date}`;
}

/**
 * "Kin Pro 💜" badge for the top of Settings. Renders only for Pro (or an
 * admin in preview mode) using the SAME flag as the rest of the app —
 * useIsPro() already folds in the live EntitlementFlipListener override, so
 * the badge appears the instant a free→pro flip is detected, no refresh. Tap
 * opens the in-app Pro/manage surface.
 */
const KinProBadge = () => {
  const navigate = useNavigate();
  const { adminPreview, session } = useAuth();
  const isAdmin = useAdmin();
  // Exact isProTier pattern used in Pro.tsx / Settings.tsx. useIsPro() carries
  // the entitlement-flip override for the live free→pro case.
  const isProNow = useIsPro() || (isAdmin && adminPreview);
  const entitlement = useEntitlement();
  const [openingPortal, setOpeningPortal] = useState(false);

  if (!isProNow) return null;

  const label = renewalLabel(entitlement);

  const handleManageSubscription = async () => {
    if (!session?.access_token) {
      navigate("/signin");
      return;
    }
    setOpeningPortal(true);
    try {
      const resp = await fetch(CREATE_PORTAL_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
          apikey: EXTERNAL_SUPABASE_PUBLISHABLE_KEY,
        },
      });
      if (!resp.ok) {
        const errData = (await resp.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
        };
        // Only the "no billing account on file" case is safe to surface verbatim
        // — it's written in brand voice server-side and tells her what to do.
        // Everything else (auth, config, Stripe API failures) is raw plumbing a
        // mom should never see, so it falls through to the generic line below.
        if (errData.error && (errData.code === "no_customer" || errData.error.includes("No billing account"))) {
          throw new PortalError(errData.error, true);
        }
        throw new PortalError(errData.error || `Error ${resp.status}`, false);
      }
      const { url } = await resp.json();
      if (!url) throw new Error("No portal URL returned");
      // Hand off through the Median external-browser bridge — never same-window
      // nav. Inside the native WKWebView, window.location.href would load the
      // Stripe portal *inside the app shell* (stranding the user, and pointing
      // an in-app webview at an external payment page). openExternal is the same
      // path checkout uses (Pro.tsx). See src/lib/openExternal.ts.
      openExternal(url);
    } catch (e) {
      console.error("[KinProBadge] portal session error:", e);
      toast.error(
        e instanceof PortalError && e.showToUser
          ? e.message
          : "Couldn't open your billing page just now. Try again in a sec?"
      );
    } finally {
      setOpeningPortal(false);
    }
  };

  return (
    <div className="flex flex-col items-center" style={{ marginTop: 8, marginBottom: 4 }}>
      <button
        type="button"
        onClick={() => navigate("/pro")}
        aria-label="Kin Pro — manage subscription"
        className="inline-flex items-center transition-all hover:opacity-90 active:scale-95"
        style={{
          backgroundColor: PERIWINKLE,
          color: "#FFFFFF",
          borderRadius: 999,
          border: "none",
          padding: "8px 18px",
          fontFamily: "'DM Serif Display', serif",
          fontSize: 16,
          cursor: "pointer",
          boxShadow: "0 2px 8px rgba(80, 80, 200, 0.25)",
        }}
      >
        Kin Pro 💜
      </button>
      {label && (
        <p
          className="font-body"
          style={{ fontSize: 12.5, color: MUTED, marginTop: 8, textAlign: "center" }}
        >
          {label}
        </p>
      )}
      <button
        type="button"
        onClick={handleManageSubscription}
        disabled={openingPortal}
        className="font-body font-semibold hover:underline"
        style={{
          fontSize: 12.5,
          color: PERIWINKLE,
          marginTop: label ? 4 : 8,
          background: "none",
          border: "none",
          cursor: openingPortal ? "default" : "pointer",
          opacity: openingPortal ? 0.6 : 1,
        }}
      >
        {openingPortal ? "Opening…" : "Manage subscription"}
      </button>
    </div>
  );
};

export default KinProBadge;
